import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";
import { useActiveLearner } from "../../hooks/useActiveLearner";
import { getStudentAuthHeaders, syncActiveLearner } from "../../utils/studentSession";

interface UniformItem {
  id: number | string;
  name: string;
  description?: string;
  price: number;
  stock: number;
  category?: string;
}

interface CartItem extends UniformItem {
  quantity: number;
  cartId: string;
}

interface Learner {
  id: number;
  name: string;
  email?: string;
  classId?: number | null;
  className?: string | null;
  category: "nursery" | "primary";
}

interface UniformOrder {
  id: number;
  orderNumber?: string;
  studentId: number;
  studentName?: string;
  className?: string;
  totalAmount?: number;
  status?: string;
  paymentStatus?: string;
  createdAt?: string;
  approvalStatus?: string;
  item?: string;
}

const UniformShop: React.FC = () => {
  const [items, setItems] = useState<UniformItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [learners, setLearners] = useState<Learner[]>([]);
  const [selectedLearnerId, setSelectedLearnerId] = useState<number | null>(null);
  const [orders, setOrders] = useState<UniformOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [cancelingId, setCancelingId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<"nursery" | "primary">("primary");

  const token = localStorage.getItem("token");
  const userName = localStorage.getItem("userName") || "Student";
  const { activeLearnerId } = useActiveLearner();

  const selectedLearner = learners.find(l => l.id === selectedLearnerId) || null;

  const fetchLearners = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl("uniform/learners"), {
        headers: getStudentAuthHeaders(token),
      });
      const list = res.data?.learners || [];
      setLearners(list);
      if (list.length > 0) {
        const defaultLearnerId = list.some((learner: Learner) => learner.id === activeLearnerId)
          ? activeLearnerId
          : list[0].id;
        setSelectedLearnerId(defaultLearnerId);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load linked learners");
    }
  }, [token, activeLearnerId]);

  const fetchInventory = useCallback(async (category: "nursery" | "primary") => {
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(getApiUrl("uniform/items"), {
        params: { grade: category },
        headers: token ? getStudentAuthHeaders(token) : undefined,
      });

      const data = Array.isArray(res.data) ? res.data : [];
      const normalized: UniformItem[] = data.map((it: any, idx: number) => ({
        id: it.id ?? it._id ?? idx,
        name: it.name ?? it.item ?? "Uniform Item",
        description: it.description ?? "",
        price: Number(it.price ?? it.unitPrice ?? 0),
        stock: Number(it.stock ?? 0),
        category: it.category ?? ""
      }));

      setItems(normalized);
    } catch (err: any) {
      console.error("Error fetching inventory:", err);
      setError(err.response?.data?.message || "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchOrders = useCallback(async () => {
    if (!token || !selectedLearner) return;
    setOrdersLoading(true);
    try {
      const res = await axios.get(getApiUrl("orders"), {
        headers: getStudentAuthHeaders(token),
        params: { studentId: selectedLearner.id }
      });
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch {
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [token, selectedLearner]);

  useEffect(() => {
    fetchLearners();
  }, [fetchLearners]);

  useEffect(() => {
    if (!selectedLearner) {
      setItems([]);
      return;
    }
    setSelectedCategory(selectedLearner.category);
    fetchInventory(selectedLearner.category);
    fetchOrders();
  }, [selectedLearner, fetchInventory, fetchOrders]);

  const filteredItems = useMemo(() => items, [items]);

  const handleQuantityChange = (itemId: string | number, delta: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const currentQty = quantities[itemId] || 1;
    const newQty = Math.max(1, Math.min(currentQty + delta, item.stock || 0));

    setQuantities(prev => ({
      ...prev,
      [itemId]: newQty
    }));
  };

  const getQuantity = (itemId: string | number) => quantities[itemId] || 1;

  const addToCart = (item: UniformItem) => {
    const quantity = getQuantity(item.id);
    if (quantity > item.stock) {
      alert("Selected quantity exceeds available stock.");
      return;
    }

    const existingIndex = cart.findIndex(c => c.id === item.id);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity = Math.min(
        newCart[existingIndex].quantity + quantity,
        item.stock || 0
      );
      setCart(newCart);
    } else {
      setCart([...cart, {
        ...item,
        quantity,
        cartId: `${item.id}-${Date.now()}`
      }]);
    }
    setQuantities(prev => ({
      ...prev,
      [item.id]: 1
    }));
  };

  const removeFromCart = (cartId: string) => {
    setCart(prev => prev.filter(i => i.cartId !== cartId));
  };

  const updateCartQuantity = (cartId: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.cartId === cartId) {
        const newQty = Math.max(1, Math.min(item.quantity + delta, item.stock || 0));
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const checkStock = async (): Promise<{available: boolean; items: any[]}> => {
    try {
      const res = await axios.post(
        getApiUrl("uniform/stock/check"),
        { items: cart.map(item => ({ id: item.id, quantity: item.quantity })) },
        { headers: token ? getStudentAuthHeaders(token) : undefined }
      );
      if (Array.isArray(res.data)) {
        const items = res.data;
        const available = items.every((it) => it.available);
        return { available, items };
      }
      return res.data;
    } catch (err) {
      return { available: false, items: [] };
    }
  };

  const placeOrder = async () => {
    if (cart.length === 0) {
      alert("Your cart is empty!");
      return;
    }
    if (!selectedLearner) {
      alert("Select a learner before placing an order.");
      return;
    }

    const stockCheck = await checkStock();
    if (!stockCheck.available) {
      alert("Some items are now out of stock. Please update your cart.");
      return;
    }

    const payload = {
      studentId: selectedLearner.id,
      classId: selectedLearner.classId || undefined,
      items: cart.map(item => ({
        id: item.id,
        quantity: item.quantity
      })),
      notes: `Order for ${selectedLearner.name} (${userName})`
    };

    try {
      setLoading(true);
      await axios.post(getApiUrl("orders"), payload, {
        headers: token ? getStudentAuthHeaders(token) : undefined,
      });

      setCart([]);
      setQuantities({});
      alert("Your uniform order has been submitted successfully! Awaiting admin approval.");
      fetchOrders();
    } catch (err: any) {
      console.error("Error placing order:", err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || "Failed to submit order";
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId: number) => {
    try {
      const confirmed = window.confirm("Are you sure you want to cancel this order? This cannot be undone.");
      if (!confirmed) return;
      setCancelingId(orderId);
      await axios.delete(getApiUrl(`orders/${orderId}`), {
        headers: token ? getStudentAuthHeaders(token) : undefined
      });
      fetchOrders();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || "Failed to cancel order.";
      alert(errorMsg);
    } finally {
      setCancelingId(null);
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/30">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900">Uniform Shop</h2>
              <p className="page-subtitle text-slate-500">Order uniforms for your child.</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="utopia-form-group">
                <label className="utopia-label">Learner</label>
                <select
                  className="utopia-input"
                  value={selectedLearnerId || ""}
                  onChange={(e) => {
                    const nextId = Number(e.target.value);
                    setSelectedLearnerId(nextId);
                    syncActiveLearner(nextId);
                  }}
                >
                  {learners.map((learner) => (
                    <option key={learner.id} value={learner.id}>
                      {learner.name} {learner.className ? `• ${learner.className}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Category</label>
                <select
                  className="utopia-input"
                  value={selectedCategory}
                  onChange={(e) => {
                    const next = e.target.value as "nursery" | "primary";
                    setSelectedCategory(next);
                    fetchInventory(next);
                  }}
                >
                  <option value="nursery">Nursery</option>
                  <option value="primary">Primary</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading uniforms...</div>
          ) : error ? (
            <div className="p-6 text-rose-600">{error}</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {filteredItems.map((item) => {
                  const quantity = getQuantity(item.id);
                  const isOutOfStock = item.stock <= 0;
                  return (
                    <div key={item.id} className="bg-white border border-slate-100 rounded-2xl p-5 flex items-center justify-between gap-4">
                      <div>
                        <h4 className="text-lg font-bold text-slate-800">{item.name}</h4>
                        {item.description && <p className="text-sm text-slate-500 mt-1">{item.description}</p>}
                        <p className="text-sm text-slate-500 mt-2">ZMW {Number(item.price).toFixed(2)}</p>
                        <span className={`inline-flex px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mt-2 ${isOutOfStock ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`}>
                          {isOutOfStock ? "Out of stock" : `In stock (${item.stock})`}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <button className="utopia-btn-icon" onClick={() => handleQuantityChange(item.id, -1)} disabled={isOutOfStock || quantity <= 1}>-</button>
                        <input
                          type="number"
                          className="utopia-input w-20 text-center"
                          value={quantity}
                          min="1"
                          max={item.stock || 0}
                          disabled={isOutOfStock}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 1;
                            setQuantities(prev => ({
                              ...prev,
                              [item.id]: Math.max(1, Math.min(val, item.stock || 0))
                            }));
                          }}
                        />
                        <button className="utopia-btn-icon" onClick={() => handleQuantityChange(item.id, 1)} disabled={isOutOfStock || quantity >= (item.stock || 0)}>+</button>
                        <button className="utopia-btn-primary" onClick={() => addToCart(item)} disabled={isOutOfStock}>
                          Add
                        </button>
                      </div>
                    </div>
                  );
                })}
                {filteredItems.length === 0 && (
                  <div className="text-center text-slate-400 py-8">No uniform items available.</div>
                )}
              </div>

              <div className="bg-white border border-slate-100 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Cart ({cartItemCount})</h3>
                {cart.length === 0 ? (
                  <p className="text-slate-400">Your cart is empty.</p>
                ) : (
                  <>
                    <div className="space-y-3">
                      {cart.map((item) => (
                        <div key={item.cartId} className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-slate-700">{item.name}</p>
                            <p className="text-xs text-slate-500">ZMW {Number(item.price).toFixed(2)} × {item.quantity}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button className="utopia-btn-icon" onClick={() => updateCartQuantity(item.cartId, -1)} disabled={item.quantity <= 1}>-</button>
                            <span className="text-sm font-bold">{item.quantity}</span>
                            <button className="utopia-btn-icon" onClick={() => updateCartQuantity(item.cartId, 1)} disabled={item.quantity >= (item.stock || 0)}>+</button>
                            <button className="utopia-btn-icon text-rose-600" onClick={() => removeFromCart(item.cartId)}>×</button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 border-t pt-4 flex justify-between font-bold">
                      <span>Total</span>
                      <span>ZMW {cartTotal.toFixed(2)}</span>
                    </div>
                    <div className="mt-4 space-y-2">
                      <button className="utopia-btn-primary w-full" onClick={placeOrder} disabled={loading}>
                        {loading ? "Processing..." : "Submit Order"}
                      </button>
                      <button className="utopia-btn-secondary w-full" onClick={() => setCart([])} disabled={loading}>
                        Clear Cart
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="utopia-page-container mt-8">
          <div className="utopia-table-header">
            <h2>Order History</h2>
          </div>
          {ordersLoading ? (
            <div className="p-6 text-center text-slate-400">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div className="p-6 text-center text-slate-400">No orders found.</div>
          ) : (
            <table className="utopia-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="group hover:bg-slate-50/60">
                    <td className="font-semibold">{order.orderNumber || `Order #${order.id}`}</td>
                    <td>{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "-"}</td>
                    <td>
                      <span
                        className={`utopia-badge text-[9px] font-black uppercase ${
                          String(order.status || "").toLowerCase() === "completed"
                            ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                            : String(order.status || "").toLowerCase() === "issued/collected"
                            ? "bg-teal-50 text-teal-600 border-teal-100"
                            : String(order.status || "").toLowerCase() === "processing"
                            ? "bg-blue-50 text-blue-600 border-blue-100"
                            : String(order.status || "").toLowerCase() === "cancelled"
                            ? "bg-rose-50 text-rose-600 border-rose-100"
                            : "bg-amber-50 text-amber-600 border-amber-100"
                        }`}
                      >
                        {order.status || "Pending"}
                      </span>
                    </td>
                    <td>ZMW {Number(order.totalAmount || 0).toFixed(2)}</td>
                    <td className="text-right">
                      {String(order.status || "").toLowerCase() === "pending" &&
                        String(order.approvalStatus || "").toLowerCase() === "pending" && (
                          <button
                            className="utopia-btn-secondary px-3 py-1 text-xs"
                            onClick={() => cancelOrder(order.id)}
                            disabled={cancelingId === order.id}
                          >
                            {cancelingId === order.id ? "Cancelling..." : "Cancel"}
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default UniformShop;
